import { Link } from "@tanstack/react-router";
import { LogOut, Settings, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/store/auth";
import { getAvatarInitials } from "@/utils/user";

/** Opens account settings and sign-out actions for the current user. */
const ProfileDropdown = () => {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);

  const handleLogout = async () => {
    signOut({
      redirectUrl: window.location.origin,
    });
  };

  if (!user) {
    return (
      <Avatar className="opacity-70 cursor-not-allowed">
        <AvatarFallback>
          <User className="w-4 h-4" />
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account menu for ${user.first_name || user.email}`}
          className="flex min-h-11 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg p-2 text-left hover:bg-background"
        >
          <Avatar className="border bg-background">
            <AvatarImage src={user.avatar_url} alt="Profile Picture" />
            <AvatarFallback>
              {getAvatarInitials({
                firstName: user.first_name,
                email: user.email,
              })}
            </AvatarFallback>
          </Avatar>
          <span className="min-w-0 text-xs">
            <span className="block truncate font-semibold">
              {user.first_name || "Account"}
            </span>
            <span className="block truncate text-muted-foreground">
              {user.email}
            </span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings className="w-4 h-4" />
            <span className="ml-2">Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="w-4 h-4 text-destructive sm:w-6 sm:h-6" />
          <span className="ml-2">Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileDropdown;
